import { CSRF_TOKEN } from './csrf_token.js'
import { DEFAULT_API_TIMEOUT_MS, isLocalApiHost, resolveApiTimeout } from './apiTimeout'

const ORGANIZER_ACCESS_TOKEN_KEY = 'organizer_access_token'
const ORGANIZER_REFRESH_TOKEN_KEY = 'organizer_refresh_token'
const AMBASSADOR_ACCESS_TOKEN_KEY = 'ambassador_access_token'
const AMBASSADOR_REFRESH_TOKEN_KEY = 'ambassador_refresh_token'
const TICKET_ACCESS_TOKEN_KEY = 'ticket_access_token'
const TICKET_REFRESH_TOKEN_KEY = 'ticket_access_refresh_token'
const LOCAL_RUNTIME = typeof window !== 'undefined' && isLocalApiHost(window.location.hostname)
const REQUEST_TIMEOUT_MS = resolveApiTimeout({ timeoutMs: DEFAULT_API_TIMEOUT_MS }, LOCAL_RUNTIME)

const isJwtToken = (token = '') => typeof token === 'string' && token.split('.').length === 3
const buildAuthorizationHeader = (token = '') => token ? { Authorization: `${isJwtToken(token) ? 'Bearer' : 'Token'} ${token}` } : {}
const readCookie = (name) => {
  if (typeof document === 'undefined' || !document.cookie) return ''
  const cookie = document.cookie.split(';').map((item) => item.trim()).find((item) => item.startsWith(`${name}=`))
  return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : ''
}
const buildCsrfHeader = () => {
  const token = readCookie('csrftoken') || CSRF_TOKEN || ''
  return token ? { 'X-CSRFTOKEN': token } : {}
}
const getTokenKeys = (authKind = 'organizer') => {
  if (authKind === 'ticket') return { access: TICKET_ACCESS_TOKEN_KEY, refresh: TICKET_REFRESH_TOKEN_KEY }
  if (authKind === 'ambassador') return { access: AMBASSADOR_ACCESS_TOKEN_KEY, refresh: AMBASSADOR_REFRESH_TOKEN_KEY }
  return { access: ORGANIZER_ACCESS_TOKEN_KEY, refresh: ORGANIZER_REFRESH_TOKEN_KEY }
}
const getStoredAccessToken = (authKind = 'organizer') => window.localStorage.getItem(getTokenKeys(authKind).access) || ''
const clearStoredTokens = (authKind = 'organizer') => { const keys = getTokenKeys(authKind); window.localStorage.removeItem(keys.access); window.localStorage.removeItem(keys.refresh) }
const inferAuthKind = (token = '') => {
  const organizerToken = window.localStorage.getItem(ORGANIZER_ACCESS_TOKEN_KEY) || ''
  const ambassadorToken = window.localStorage.getItem(AMBASSADOR_ACCESS_TOKEN_KEY) || ''
  const ticketToken = window.localStorage.getItem(TICKET_ACCESS_TOKEN_KEY) || ''
  if (token && ticketToken && token === ticketToken) return 'ticket'
  if (token && ambassadorToken && token === ambassadorToken) return 'ambassador'
  if (token && organizerToken && token === organizerToken) return 'organizer'
  if (!token && organizerToken) return 'organizer'
  if (!token && ambassadorToken) return 'ambassador'
  if (!token && ticketToken) return 'ticket'
  return 'organizer'
}

const resolveTimeoutMs = (options = {}) => resolveApiTimeout(options, LOCAL_RUNTIME)

async function parseResponse(response) {
  if (response.status === 204) return ''
  if (response.status === 404) return null
  const contentType = response.headers.get('content-type') || ''
  const payload = contentType.includes('application/json') ? await response.json() : await response.text()
  if (response.ok) return payload
  if (payload && typeof payload === 'object') return payload
  if (response.status >= 500) return { err: `El servidor no respondio a tiempo (${response.status}).` }
  return { err: `La solicitud fallo (${response.status}).` }
}

function fetchWithTimeout(endpoint, config = {}, timeoutMs = REQUEST_TIMEOUT_MS) {
  if (timeoutMs === null || timeoutMs === false || timeoutMs === 0) {
    return fetch(endpoint, config)
  }

  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)

  return fetch(endpoint, { ...config, signal: controller.signal })
    .finally(() => {
      window.clearTimeout(timer)
    })
}

const buildJsonConfig = (method, data, headers = {}) => ({ method: method || 'GET', credentials: 'same-origin', body: data !== undefined ? JSON.stringify(data) : null, headers: { 'content-type': 'application/json', ...buildCsrfHeader(), ...headers } })
const buildFormConfig = (method, data, headers = {}) => ({ method: method || 'GET', credentials: 'same-origin', body: data, headers: { ...buildCsrfHeader(), ...headers } })

async function refreshJwtToken(authKind = 'organizer') {
  const keys = getTokenKeys(authKind)
  const refreshToken = window.localStorage.getItem(keys.refresh) || ''
  if (!isJwtToken(refreshToken)) { clearStoredTokens(authKind); return '' }
  const response = await fetchWithTimeout('/api/auth/token/refresh/', buildJsonConfig('POST', { refresh: refreshToken }), 10000)
  const payload = await parseResponse(response)
  if (!response.ok || !payload || !payload.access) { clearStoredTokens(authKind); return '' }
  window.localStorage.setItem(keys.access, payload.access)
  if (payload.refresh) window.localStorage.setItem(keys.refresh, payload.refresh)
  return payload.access
}

async function requestWithAuth(endpoint, config, options = {}) {
  const authKind = options.authKind || 'organizer'
  const timeoutMs = resolveTimeoutMs(options)
  let activeToken = options.token || getStoredAccessToken(authKind)
  let requestConfig = { ...config, headers: { ...(config.headers || {}), ...buildAuthorizationHeader(activeToken) } }
  let response = await fetchWithTimeout(endpoint, requestConfig, timeoutMs)
  if ((response.status === 401 || response.status === 403) && isJwtToken(activeToken)) {
    const refreshedAccess = await refreshJwtToken(authKind)
    if (refreshedAccess) {
      activeToken = refreshedAccess
      requestConfig = { ...config, headers: { ...(config.headers || {}), ...buildAuthorizationHeader(activeToken) } }
      response = await fetchWithTimeout(endpoint, requestConfig, timeoutMs)
    }
  }
  return parseResponse(response)
}

function apiService(endpoint, method, data, options = {}) {
  return requestWithAuth(endpoint, buildJsonConfig(method, data, options.headers || {}), { authKind: 'organizer', timeoutMs: resolveTimeoutMs(options) }).catch((error) => error && error.name === 'AbortError' ? ({ err: 'La solicitud tardo demasiado. Intenta de nuevo.' }) : ({ err: 'No pudimos conectar con el servidor.' }))
}

function apiServiceAuth(endpoint, method, data, token, authKind, options = {}) {
  return requestWithAuth(endpoint, buildJsonConfig(method, data, options.headers || {}), { token: token || '', authKind: authKind || inferAuthKind(token || ''), timeoutMs: resolveTimeoutMs(options) }).catch((error) => error && error.name === 'AbortError' ? ({ err: 'La solicitud tardo demasiado. Intenta de nuevo.' }) : ({ err: 'No pudimos conectar con el servidor.' }))
}

function apiServiceAuthForm(endpoint, method, data, token, authKind, options = {}) {
  return requestWithAuth(endpoint, buildFormConfig(method, data, options.headers || {}), { token: token || '', authKind: authKind || inferAuthKind(token || ''), timeoutMs: resolveTimeoutMs(options) }).catch((error) => error && error.name === 'AbortError' ? ({ err: 'La solicitud tardo demasiado. Intenta de nuevo.' }) : ({ err: 'No pudimos conectar con el servidor.' }))
}

function apiServiceSession(endpoint, method, data, options = {}) {
  return fetchWithTimeout(endpoint, buildJsonConfig(method, data, options.headers || {}), resolveTimeoutMs(options)).then(parseResponse).catch((error) => error && error.name === 'AbortError' ? ({ err: 'La solicitud tardo demasiado. Intenta de nuevo.' }) : ({ err: 'No pudimos conectar con el servidor.' }))
}

function apiServiceForm(endpoint, method, data, options = {}) {
  return requestWithAuth(endpoint, buildFormConfig(method, data, options.headers || {}), { authKind: 'organizer', timeoutMs: resolveTimeoutMs(options) }).catch((error) => error && error.name === 'AbortError' ? ({ err: 'La solicitud tardo demasiado. Intenta de nuevo.' }) : ({ err: 'No pudimos conectar con el servidor.' }))
}

export { apiService, apiServiceAuth, apiServiceAuthForm, apiServiceForm, apiServiceSession }
